import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useAuth } from "../../contexts/AuthContext";
import { useEffect, useState } from "react";
import { db } from "../../firebase";
import AuthenticationDocs from "./components/AuthenticationDocs";
import DocSection from "./components/DocSection";
import { Key } from "../../models/Key";

const DocsPage: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<Key[]>();

  useEffect(() => {
    const docRef = query(
      collection(db, "keys"),
      where("userId", "==", user?.uid)
    );
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        const items = snapshot.docs.map(
          (doc) => ({ id: doc.id, ...doc.data() }) as Key
        );
        setItems(items);
      },
      (err) => {
        console.error(err);
      }
    );
    return () => {
      unsubscribe();
    };
  }, [user?.uid]);

  const endpoints = [
    {
      name: "Search Artists",
      method: "GET" as const,
      path: "/artists",
      description: "Search for artists by name. Returns matching artists with basic information.",
      queryParameters: [
        { name: "name", description: "The name of the artist to search for", required: true },
      ],
      example: `{
  "data": [
    {
      "id": "5a7b18bc9c7e0400146c7802",
      "name": "Andy Warhol"
    }
  ]
}`,
    },
    {
      name: "Artist Auction Records",
      method: "GET" as const,
      path: "/artists/:id/past_auction_records",
      description: "Retrieve paginated auction records for a specific artist, including sale prices, estimates, and images.",
      queryParameters: [
        { name: "page", description: "Page number for pagination", required: false },
        { name: "sortBy", description: "Sort field: sale_date or hammer_price", required: false },
      ],
      example: `{
  "data": [
    {
      "_id": "5ce99c8a1cca2f00174f8b4f",
      "title": "In The Bottom Of My Garden",
      "hammer_price": 225000,
      "estimate_low": 250000,
      "estimate_high": 350000,
      "sale_date": "2017-10-17T19:00:00.000Z",
      "auction_house": "Phillips"
    }
  ],
  "totalDocs": 3066,
  "page": 1,
  "totalPages": 123
}`,
    },
    {
      name: "Artist Sell-Through Rates",
      method: "GET" as const,
      path: "/artists/:id/sell_through_rates",
      description: "Get yearly sell-through rate statistics for an artist, showing lots sold vs. total lots offered.",
      example: `{
  "data": [
    {
      "_id": 2023,
      "number_of_lots_sold": 45,
      "number_of_lots_total": 52
    }
  ]
}`,
    },
    {
      name: "Artwork Valuation",
      method: "POST" as const,
      path: "/valuation",
      description: "Get AI-powered price estimates for an artwork based on artist, dimensions, and category.",
      bodyParameters: [
        { name: "artist", description: "Artist ID from the artists endpoint", required: true },
        { name: "width", description: "Width of the artwork in centimeters", required: true },
        { name: "height", description: "Height of the artwork in centimeters", required: true },
        { name: "category", description: "Category: painting, print, sculpture, photograph", required: true },
      ],
      example: `{
  "data": {
    "currency": "USD",
    "high": "3645165.82",
    "average": "3228603.80",
    "low": "2812041.78"
  }
}`,
    },
    {
      name: "Price Database Search",
      method: "GET" as const,
      path: "/price_database",
      description: "Search auction records with advanced filtering options and market analytics.",
      queryParameters: [
        { name: "artist", description: "Filter by artist ID", required: false },
        { name: "category", description: "Filter by category", required: false },
        { name: "min_hammer_price", description: "Minimum hammer price in USD", required: false },
        { name: "max_hammer_price", description: "Maximum hammer price in USD", required: false },
        { name: "include_analytics", description: "Include market analytics (true/false)", required: false },
      ],
      example: `{
  "data": [...],
  "stats": {
    "count": 1250,
    "totalValue": 45000000,
    "avgPrice": 36000,
    "medianPrice": 28000
  },
  "analytics": {
    "performance": { "cagr": 8.5 },
    "risk": { "volatility": 22.3 }
  }
}`,
    },
    {
      name: "Market Analytics",
      method: "POST" as const,
      path: "/analytics/predict",
      description: "Get Monte Carlo price predictions with probability distributions and financial metrics.",
      bodyParameters: [
        { name: "artist", description: "Artist ID", required: true },
        { name: "category", description: "Artwork category", required: true },
        { name: "width", description: "Width in centimeters", required: true },
        { name: "height", description: "Height in centimeters", required: true },
      ],
      example: `{
  "data": {
    "currentPrice": 150000,
    "predictions": {
      "fiveYear": {
        "conservative": 165000,
        "moderate": 195000,
        "optimistic": 245000
      }
    },
    "modelDetails": {
      "cagr": 5.4,
      "volatility": 18.2
    }
  }
}`,
    },
  ];

  return (
    <div className="min-h-screen bg-gallery-50">
      {/* Header */}
      <div className="bg-white border-b border-gallery-200">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-3">
            Developer Resources
          </p>
          <h1 className="font-display text-display-sm text-gallery-900 mb-3">
            API Documentation
          </h1>
          <p className="text-gallery-600 max-w-2xl">
            Complete reference for integrating with the Art Collection API.
            Access auction records, market analytics, and AI-powered valuations.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Quick Start */}
        <div className="bg-gallery-900 rounded-sm p-8 mb-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="font-display text-xl text-white mb-2">Get Started</h2>
              <p className="text-gallery-300 text-sm">
                Generate an API key to make your first request in minutes.
              </p>
            </div>
            <div className="flex gap-3">
              <a
                href="/keys"
                className="inline-flex items-center px-4 py-2.5 bg-white text-gallery-900 text-sm font-medium rounded-sm hover:bg-gallery-100 transition-colors"
              >
                Get API Key
              </a>
              <a
                href="#authentication"
                className="inline-flex items-center px-4 py-2.5 border border-gallery-600 text-white text-sm font-medium rounded-sm hover:bg-gallery-800 transition-colors"
              >
                View Examples
              </a>
            </div>
          </div>
        </div>

        {/* Feature Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {[
            {
              title: "AI Valuations",
              description: "Monte Carlo price predictions with confidence intervals",
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              ),
            },
            {
              title: "Market Analytics",
              description: "CAGR, Sharpe ratio, volatility, and liquidity metrics",
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              title: "1M+ Records",
              description: "Comprehensive auction data with advanced filtering",
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              ),
            },
          ].map((feature, idx) => (
            <div key={idx} className="bg-white border border-gallery-200 rounded-sm p-5">
              <div className="w-10 h-10 rounded-sm bg-gallery-100 flex items-center justify-center text-gallery-600 mb-4">
                {feature.icon}
              </div>
              <h3 className="font-medium text-gallery-900 mb-1">{feature.title}</h3>
              <p className="text-sm text-gallery-500">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Authentication Section */}
        <div id="authentication" className="mb-10">
          <AuthenticationDocs keys={items ?? []} />
        </div>

        {/* API Endpoints */}
        <div className="mb-6">
          <h2 className="font-display text-lg text-gallery-900 mb-2">API Endpoints</h2>
          <p className="text-sm text-gallery-500">Available endpoints for accessing art market data</p>
        </div>

        <div className="space-y-4">
          {endpoints.map((endpoint, idx) => (
            <DocSection key={idx} {...endpoint} />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 p-6 bg-gallery-100 rounded-sm">
          <p className="text-xs text-gallery-500 leading-relaxed">
            <strong className="text-gallery-700">Rate Limits:</strong> API requests are limited to 100 requests per minute.
            For higher limits, contact us at <a href="mailto:api@artcollection.io" className="text-gallery-700 underline">api@artcollection.io</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;
